import { createContext, useCallback, useContext, useRef, useState } from "react";

/* The one-line confirmation the board answers a save with. Same behaviour
   as the single-file build: it shows for 2.4s, and a failure stays red. */
const ToastContext = createContext(() => {});

export function ToastProvider({ children }) {
  const [note, setNote] = useState(null);
  const timer = useRef(0);

  const dismiss = useCallback(() => {
    clearTimeout(timer.current);
    setNote(null);
  }, []);

  const show = useCallback((message, bad = false) => {
    clearTimeout(timer.current);
    let payload;
    if (typeof message === "object" && message !== null) {
      const isBad = Boolean(message.bad || message.tone === "bad");
      const duration = message.duration ?? (isBad ? 5000 : 3500);
      payload = {
        title: message.title || (isBad ? "Action Failed" : "Notification"),
        message: message.message || "",
        bad: isBad,
        duration,
        key: Date.now(),
      };
      setNote(payload);
      if (duration > 0) {
        timer.current = setTimeout(() => setNote(null), duration);
      }
    } else {
      const isBad = Boolean(bad);
      const strMsg = String(message || "");
      let title = isBad ? "Action Failed" : "Success";
      if (!isBad) {
        if (/saved/i.test(strMsg)) title = "Board Saved";
        else if (/discard/i.test(strMsg)) title = "Edits Discarded";
        else if (/delete/i.test(strMsg)) title = "Board Deleted";
        else if (/export/i.test(strMsg)) title = "Export Complete";
        else if (/import/i.test(strMsg)) title = "Import Complete";
        else if (/reset/i.test(strMsg)) title = "Board Reset";
        else if (/acknowledge/i.test(strMsg)) title = "Acknowledged";
      }
      const duration = isBad ? 5000 : 3500;
      payload = {
        title,
        message: strMsg,
        bad: isBad,
        duration,
        key: Date.now(),
      };
      setNote(payload);
      timer.current = setTimeout(() => setNote(null), duration);
    }
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <aside
        className={"toast toast-card" + (note ? " show" : "") + (note?.bad ? " bad" : "")}
        role="status"
        aria-live="polite"
      >
        <div
          key={note?.key}
          className="toast-accent-line"
          style={note?.duration ? { animationDuration: `${note.duration}ms` } : undefined}
        />
        <div className="toast-body">
          <div className="toast-icon-wrap" aria-hidden="true">
            {note?.bad ? (
              <span className="toast-icon-circle bad">!</span>
            ) : (
              <span className="toast-icon-circle ok">✓</span>
            )}
          </div>
          <div className="toast-text">
            <div className="toast-title">{note?.title || "Notification"}</div>
            <div className="toast-msg">{note?.message || ""}</div>
          </div>
          <button
            type="button"
            className="toast-close"
            aria-label="Dismiss notification"
            onClick={dismiss}
          >
            ×
          </button>
        </div>
      </aside>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
